import { Box, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getTranslationValue } from '../../../constants/posMenu';
import {
  buildPathKey,
  buildOptionPathKey,
  buildOptionEntry,
  resolveParentOptionInfo,
  removeNestedGroupsForPrefix,
  isOptionPreselected,
  calculatePriceByType,
  getEffectivePriceType,
  PRICE_TYPES,
  isItemSoldOut,
  isMultiplierConditionActive,
  getEffectiveOptionsCount,
  getEffectiveOptionCount,
  computeTotalItemCount,
  computeActualQtyUpdate,
  applyAncestorDispatch,
  findAllMgInstances,
  shouldDisableForAncestorMax,
  hasModifierItemMinimums,
  meetsModifierItemMinimums,
} from './previewHelpers';
import { POS_COLORS } from './posColors';

/**
 * ModifierGroupsPanel – renders modifier groups matching POS client v1 layout:
 *   - Groups side by side (horizontal row, wrapping)
 *   - Each group: gray header + column-first grid of square-ish option buttons
 *   - Options flow top→bottom (max 3 per column), then next column
 *   - Each button ≈ 78×74px with 4px gap
 */
const ModifierGroupsPanel = ({
  menuMap,
  selectedProduct,
  modifierGroups,
  onUpdateModifierGroups,
  onNavigateInto,
  onOpenNestedModal,
  currentOptionPathKey,
  pendingOptionItem,
}) => {
  if (!selectedProduct) return null;

  const isAtProductLevel =
    !pendingOptionItem || pendingOptionItem._id === selectedProduct._id;
  const modifierGroupIds = isAtProductLevel
    ? selectedProduct.modifierGroupIds || []
    : pendingOptionItem?.modifierGroupIds || [];
  const effectiveParentPK = isAtProductLevel ? null : currentOptionPathKey;

  if (modifierGroupIds.length === 0) return null;

  const { parentOption, isNested: isNestedLevel } = resolveParentOptionInfo(
    currentOptionPathKey,
    modifierGroups
  );

  // -----------------------------------------------------------------------
  // Handle option click – mirrors POS client handleSelectModifierGroupOption
  // -----------------------------------------------------------------------
  const handleOptionClick = (mgDef, pathKey, optionItem) => {
    if (isItemSoldOut(optionItem)) return;

    let updatedGroups = { ...modifierGroups };
    const mgSelection = updatedGroups[pathKey];
    const currentOptions = mgSelection
      ? { ...mgSelection.modifierOptions }
      : {};

    const optionId = optionItem._id;
    const isAlreadySelected = !!currentOptions[optionId];
    const hasNested =
      optionItem.modifierGroupIds && optionItem.modifierGroupIds.length > 0;
    const optionPathKey = buildOptionPathKey(pathKey, optionId);

    const clickMgMultiplierCond = {
      applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
      priceType: mgDef.priceType,
    };
    const clickMgMultiplierActive = isMultiplierConditionActive(clickMgMultiplierCond);

    const { entry: optEntry, priceResult } = buildOptionEntry({
      optionItem,
      menuMap,
      modifierGroup: mgDef,
      parentOption: isNestedLevel ? parentOption : null,
      isNestedUnderParentOption: isNestedLevel,
      optionPathKey,
    });

    // Check showNestedLayout - MG level takes priority over Item level
    // Values: null/undefined/"default" = default navigation, "popup" = show in popup
    const nestedLayout = mgDef.showNestedLayout ?? optionItem.showNestedLayout;

    // --- A1) Has nested modifiers + popup layout → open NestedLayoutModal
    if (hasNested && nestedLayout === 'popup') {
      if (!isAlreadySelected) {
        const a1ShouldReplaceAll = (() => {
          if (clickMgMultiplierActive && (Number(mgDef.max) || 0)) {
            const curEff = getEffectiveOptionsCount(
              Object.values(currentOptions),
              mgDef.applyValidationToItemsActualQuantity,
              clickMgMultiplierCond
            );
            const newWt = priceResult.originalPrice ?? 0;
            return curEff + newWt > (Number(mgDef.max) || 0);
          }
          return (Number(mgDef.max) || 0) === 1 && !clickMgMultiplierActive;
        })();

        if (a1ShouldReplaceAll) {
          Object.keys(currentOptions).forEach((oldOptId) => {
            if (oldOptId !== optionId) {
              const oldOptionBranchPrefix = buildOptionPathKey(pathKey, oldOptId);
              updatedGroups = removeNestedGroupsForPrefix(
                updatedGroups,
                oldOptionBranchPrefix
              );
            }
          });
          updatedGroups[pathKey] = {
            ...mgDef,
            pathKey,
            hideNameOnOrder:
              menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
              mgDef.hideNameOnOrder,
            inOrderViewLayout:
              menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
              mgDef.inOrderViewLayout,
            sortParentOptionsByThisOptionsSorting:
              menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
              mgDef.sortParentOptionsByThisOptionsSorting,
            groupAllParentsByThisOption:
              menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
              mgDef.groupAllParentsByThisOption,
            modifierOptions: { [optionId]: optEntry },
          };
        } else {
          updatedGroups[pathKey] = {
            ...mgDef,
            pathKey,
            hideNameOnOrder:
              menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
              mgDef.hideNameOnOrder,
            inOrderViewLayout:
              menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
              mgDef.inOrderViewLayout,
            sortParentOptionsByThisOptionsSorting:
              menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
              mgDef.sortParentOptionsByThisOptionsSorting,
            groupAllParentsByThisOption:
              menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
              mgDef.groupAllParentsByThisOption,
            modifierOptions: { ...currentOptions, [optionId]: optEntry },
          };
        }

        if (
          priceResult.shouldZeroParentPrice &&
          isNestedLevel &&
          parentOption
        ) {
          applyZeroParentPrice(
            updatedGroups,
            currentOptionPathKey,
            parentOption._id,
            updatedGroups[pathKey]?.modifierOptions
          );
        }

        onUpdateModifierGroups(updatedGroups, {
          selectedOptionId: optionId,
          parentMgPathKey: pathKey,
          selectedOptionData: optEntry,
        });
      }

      if (onOpenNestedModal) {
        onOpenNestedModal({
          optionItem,
          parentMgDef: mgDef,
          parentMgPathKey: pathKey,
          optionPathKey,
        });
      }
      return;
    }

    // --- A2) Has nested modifiers + default navigation → navigate into nested view
    if (hasNested) {
      if (!isAlreadySelected) {
        const a2ShouldReplaceAll = (() => {
          if (clickMgMultiplierActive && (Number(mgDef.max) || 0)) {
            const curEff = getEffectiveOptionsCount(
              Object.values(currentOptions),
              mgDef.applyValidationToItemsActualQuantity,
              clickMgMultiplierCond
            );
            const newWt = priceResult.originalPrice ?? 0;
            return curEff + newWt > (Number(mgDef.max) || 0);
          }
          return (Number(mgDef.max) || 0) === 1 && !clickMgMultiplierActive;
        })();

        if (a2ShouldReplaceAll) {
          Object.keys(currentOptions).forEach((oldOptId) => {
            if (oldOptId !== optionId) {
              const oldOptionBranchPrefix = buildOptionPathKey(pathKey, oldOptId);
              updatedGroups = removeNestedGroupsForPrefix(
                updatedGroups,
                oldOptionBranchPrefix
              );
            }
          });
          updatedGroups[pathKey] = {
            ...mgDef,
            pathKey,
            hideNameOnOrder:
              menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
              mgDef.hideNameOnOrder,
            inOrderViewLayout:
              menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
              mgDef.inOrderViewLayout,
            sortParentOptionsByThisOptionsSorting:
              menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
              mgDef.sortParentOptionsByThisOptionsSorting,
            groupAllParentsByThisOption:
              menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
              mgDef.groupAllParentsByThisOption,
            modifierOptions: { [optionId]: optEntry },
          };
        } else {
          updatedGroups[pathKey] = {
            ...mgDef,
            pathKey,
            hideNameOnOrder:
              menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
              mgDef.hideNameOnOrder,
            inOrderViewLayout:
              menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
              mgDef.inOrderViewLayout,
            sortParentOptionsByThisOptionsSorting:
              menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
              mgDef.sortParentOptionsByThisOptionsSorting,
            groupAllParentsByThisOption:
              menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
              mgDef.groupAllParentsByThisOption,
            modifierOptions: { ...currentOptions, [optionId]: optEntry },
          };
        }

        if (
          priceResult.shouldZeroParentPrice &&
          isNestedLevel &&
          parentOption
        ) {
          applyZeroParentPrice(
            updatedGroups,
            currentOptionPathKey,
            parentOption._id,
            updatedGroups[pathKey]?.modifierOptions
          );
        }

        onUpdateModifierGroups(updatedGroups, {
          selectedOptionId: optionId,
          parentMgPathKey: pathKey,
          selectedOptionData: optEntry,
        });
      }

      if (onNavigateInto) {
        onNavigateInto(optionItem, mgDef._id, optionPathKey);
      }
      return;
    }

    // --- B) Already selected: allow toggle off (unless preselected)
    if (isAlreadySelected) {
      const isPreselected = isOptionPreselected(
        selectedProduct,
        pathKey,
        optionId
      );
      if (isPreselected && hasNested) {
        // Open popup for preselected nested options
        if (onOpenNestedModal) {
          onOpenNestedModal({
            optionItem,
            parentMgDef: mgDef,
            parentMgPathKey: pathKey,
            optionPathKey,
          });
        }
        return;
      }
      if (isPreselected) return; // keep preselected leaf in selection, do not toggle off

      const unselectedOpt = currentOptions[optionId];

      if (
        unselectedOpt?.priceType === 'multiplierApplyingToParentPrice' &&
        isNestedLevel &&
        parentOption
      ) {
        const otherHasMultiplier = Object.values(currentOptions).some(
          (o) =>
            o._id !== optionId &&
            o.priceType === 'multiplierApplyingToParentPrice'
        );
        if (!otherHasMultiplier) {
          restoreParentPrice(updatedGroups, currentOptionPathKey, parentOption);
        } else {
          const segs = currentOptionPathKey.split('__');
          let parentMgPK = null;
          for (let i = segs.length - 1; i >= 0; i--) {
            if (segs[i].startsWith('op_')) {
              parentMgPK = segs.slice(0, i).join('__');
              break;
            }
          }
          if (parentMgPK && updatedGroups[parentMgPK]?.modifierOptions?.[parentOption._id]) {
            const parentOptData = updatedGroups[parentMgPK].modifierOptions[parentOption._id];
            const remainingMultiplierChildren = Object.values(currentOptions)
              .filter((o) => o._id !== optionId && o.priceType === 'multiplierApplyingToParentPrice')
              .map((o) => ({ count: o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
            const parentBaseAQ = parentOptData.calculatedActualQuantity ?? parentOptData.actualQuantityWithMultiplier ?? parentOptData.originalPrice ?? 0;
            const aqResult = computeActualQtyUpdate({
              parentMgPathKey: parentMgPK,
              parentOptionId: parentOption._id,
              parentBaseActualQty: parentBaseAQ,
              multiplierChildren: remainingMultiplierChildren,
              selectedModifierGroups: updatedGroups,
              menuItems: menuMap?.items ?? {},
              menuModifierGroups: menuMap?.modifierGroups ?? {},
            });
            updatedGroups[parentMgPK] = {
              ...updatedGroups[parentMgPK],
              modifierOptions: {
                ...updatedGroups[parentMgPK].modifierOptions,
                [parentOption._id]: {
                  ...parentOptData,
                  price: 0,
                  actualQuantityWithMultiplier: 0,
                },
              },
            };
            applyAncestorDispatch(updatedGroups, aqResult);
          }
        }
      }

      delete currentOptions[optionId];
      updatedGroups = removeNestedGroupsForPrefix(updatedGroups, optionPathKey);

      if (Object.keys(currentOptions).length === 0) {
        delete updatedGroups[pathKey];
      } else {
        updatedGroups[pathKey] = {
          ...updatedGroups[pathKey],
          modifierOptions: currentOptions,
        };
      }
      onUpdateModifierGroups(updatedGroups);
      return;
    }

    // --- C) Leaf: compute shouldReplaceAll (covers max===1 single-select AND multiplier weight exceeds)
    const shouldReplaceAll = (() => {
      if (clickMgMultiplierActive && (Number(mgDef.max) || 0)) {
        const curEff = getEffectiveOptionsCount(
          Object.values(currentOptions),
          mgDef.applyValidationToItemsActualQuantity,
          clickMgMultiplierCond
        );
        const newWt = priceResult.originalPrice ?? 0;
        return curEff + newWt > (Number(mgDef.max) || 0);
      }
      return (Number(mgDef.max) || 0) === 1;
    })();

    if (shouldReplaceAll) {
      Object.keys(currentOptions).forEach((oldOptId) => {
        if (oldOptId !== optionId) {
          const oldOpt = currentOptions[oldOptId];
          if (
            oldOpt?.priceType === 'multiplierApplyingToParentPrice' &&
            isNestedLevel &&
            parentOption
          ) {
            restoreParentPrice(
              updatedGroups,
              currentOptionPathKey,
              parentOption
            );
          }
          const oldOptionBranchPrefix = buildOptionPathKey(pathKey, oldOptId);
          updatedGroups = removeNestedGroupsForPrefix(
            updatedGroups,
            oldOptionBranchPrefix
          );
        }
      });
      updatedGroups[pathKey] = {
        ...mgDef,
        pathKey,
        hideNameOnOrder:
          menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
          mgDef.hideNameOnOrder,
        inOrderViewLayout:
          menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
          mgDef.inOrderViewLayout,
        sortParentOptionsByThisOptionsSorting:
          menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
          mgDef.sortParentOptionsByThisOptionsSorting,
        groupAllParentsByThisOption:
          menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
          mgDef.groupAllParentsByThisOption,
        modifierOptions: { [optionId]: optEntry },
      };

      if (priceResult.shouldZeroParentPrice && isNestedLevel && parentOption) {
        applyZeroParentPrice(
          updatedGroups,
          currentOptionPathKey,
          parentOption._id,
          updatedGroups[pathKey]?.modifierOptions
        );
      }

      onUpdateModifierGroups(updatedGroups);
      return;
    }

    // --- D) Leaf option: multi-select → add
    if ((Number(mgDef.max) || 0) > 0) {
      const currentCount = getEffectiveOptionsCount(
        Object.values(currentOptions),
        mgDef.applyValidationToItemsActualQuantity,
        clickMgMultiplierCond
      );
      if (currentCount >= (Number(mgDef.max) || 0)) return;
    }

    currentOptions[optionId] = optEntry;
    updatedGroups[pathKey] = {
      ...mgDef,
      pathKey,
      hideNameOnOrder:
        menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
        mgDef.hideNameOnOrder,
      inOrderViewLayout:
        menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
        mgDef.inOrderViewLayout,
      sortParentOptionsByThisOptionsSorting:
        menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
        mgDef.sortParentOptionsByThisOptionsSorting,
      groupAllParentsByThisOption:
        menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
        mgDef.groupAllParentsByThisOption,
      modifierOptions: currentOptions,
    };

    if (priceResult.shouldZeroParentPrice && isNestedLevel && parentOption) {
      applyZeroParentPrice(
        updatedGroups,
        currentOptionPathKey,
        parentOption._id,
        updatedGroups[pathKey]?.modifierOptions
      );
    }

    onUpdateModifierGroups(updatedGroups, {
      selectedOptionId: optionId,
      parentMgPathKey: pathKey,
      selectedOptionData: optEntry,
    });
  };

  // -----------------------------------------------------------------------
  // Increment / decrement (canSelectMultipleOptions)
  // -----------------------------------------------------------------------
  const handleIncrement = (mgDef, pathKey, optionId) => {
    const incMgCond = {
      applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
      priceType: mgDef.priceType,
    };
    if (isMultiplierConditionActive(incMgCond)) return;

    const updatedGroups = { ...modifierGroups };
    const mg = updatedGroups[pathKey];
    if (!mg) return;
    const opts = { ...mg.modifierOptions };
    const opt = opts[optionId];
    if (!opt) return;

    const maxPer = mgDef.maxAnySingleModifierItems || 0;
    if (maxPer > 0 && (opt.count || 0) >= maxPer) return;
    const max = mgDef.max || 0;
    const total = Object.values(opts).reduce((s, o) => s + (o.count || 0), 0);
    if (max > 0 && total >= max) return;

    opts[optionId] = { ...opt, count: (opt.count || 0) + 1 };
    updatedGroups[pathKey] = { ...mg, modifierOptions: opts };

    // Recompute parent actualQuantityWithMultiplier if this is a MATP option
    if (opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE && currentOptionPathKey) {
      const segs = currentOptionPathKey.split('__');
      let parentMgPK = null;
      for (let i = segs.length - 1; i >= 0; i--) {
        if (segs[i].startsWith('op_')) { parentMgPK = segs.slice(0, i).join('__'); break; }
      }
      if (parentMgPK) {
        const parentOptId = currentOptionPathKey.split('__').filter((s) => s.startsWith('op_')).pop()?.replace('op_', '');
        const parentMg = updatedGroups[parentMgPK];
        const parentOpt = parentOptId ? parentMg?.modifierOptions?.[parentOptId] : null;
        if (parentOpt) {
          const multiplierChildren = Object.values(opts)
            .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
            .map((o) => ({ count: o._id === optionId ? (opt.count || 0) + 1 : o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
          const parentBaseAQ = parentOpt.calculatedActualQuantity ?? parentOpt.actualQuantityWithMultiplier ?? parentOpt.originalPrice ?? 0;
          const aqResult = computeActualQtyUpdate({
            parentMgPathKey: parentMgPK,
            parentOptionId: parentOptId,
            parentBaseActualQty: parentBaseAQ,
            multiplierChildren,
            selectedModifierGroups: updatedGroups,
            menuItems: menuMap?.items ?? {},
            menuModifierGroups: menuMap?.modifierGroups ?? {},
          });
          updatedGroups[parentMgPK] = {
            ...parentMg,
            modifierOptions: {
              ...parentMg.modifierOptions,
              [parentOptId]: { ...parentOpt, price: 0, actualQuantityWithMultiplier: 0 },
            },
          };
          applyAncestorDispatch(updatedGroups, aqResult);
        }
      }
    }

    onUpdateModifierGroups(updatedGroups);
  };

  const handleDecrement = (mgDef, pathKey, optionId) => {
    const decMgCond = {
      applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
      priceType: mgDef.priceType,
    };
    if (isMultiplierConditionActive(decMgCond)) return;

    const updatedGroups = { ...modifierGroups };
    const mg = updatedGroups[pathKey];
    if (!mg) return;
    const opts = { ...mg.modifierOptions };
    const opt = opts[optionId];
    if (!opt) return;

    if ((opt.count || 0) > 1) {
      opts[optionId] = { ...opt, count: opt.count - 1 };
    } else {
      delete opts[optionId];
      const optPK = buildOptionPathKey(pathKey, optionId);
      Object.keys(updatedGroups).forEach((pk) => {
        if (pk.startsWith(optPK + '__')) delete updatedGroups[pk];
      });
    }

    if (Object.keys(opts).length === 0) {
      delete updatedGroups[pathKey];
    } else {
      updatedGroups[pathKey] = { ...mg, modifierOptions: opts };
    }

    // Recompute parent actualQuantityWithMultiplier if this is a MATP option
    if (opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE && currentOptionPathKey) {
      const segs = currentOptionPathKey.split('__');
      let parentMgPK = null;
      for (let i = segs.length - 1; i >= 0; i--) {
        if (segs[i].startsWith('op_')) { parentMgPK = segs.slice(0, i).join('__'); break; }
      }
      if (parentMgPK && updatedGroups[parentMgPK]) {
        const parentOptId = currentOptionPathKey.split('__').filter((s) => s.startsWith('op_')).pop()?.replace('op_', '');
        const parentMg = updatedGroups[parentMgPK];
        const parentOpt = parentOptId ? parentMg?.modifierOptions?.[parentOptId] : null;
        if (parentOpt) {
          const currentCount = opt.count || 0;
          const newCount = currentCount > 1 ? currentCount - 1 : 0;
          const multiplierChildren = Object.values(opts)
            .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE && (o._id !== optionId || newCount > 0))
            .map((o) => ({ count: o._id === optionId ? newCount : o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
          const parentBaseAQ = parentOpt.calculatedActualQuantity ?? parentOpt.actualQuantityWithMultiplier ?? parentOpt.originalPrice ?? 0;
          const aqResult = computeActualQtyUpdate({
            parentMgPathKey: parentMgPK,
            parentOptionId: parentOptId,
            parentBaseActualQty: parentBaseAQ,
            multiplierChildren,
            selectedModifierGroups: updatedGroups,
            menuItems: menuMap?.items ?? {},
            menuModifierGroups: menuMap?.modifierGroups ?? {},
          });
          updatedGroups[parentMgPK] = {
            ...parentMg,
            modifierOptions: {
              ...parentMg.modifierOptions,
              [parentOptId]: {
                ...parentOpt,
                price: multiplierChildren.length > 0 ? 0 : (parentOpt.calculatedPrice ?? parentOpt.originalPrice ?? 0),
                actualQuantityWithMultiplier: 0,
              },
            },
          };
          applyAncestorDispatch(updatedGroups, aqResult);
        }
      }
    }

    onUpdateModifierGroups(updatedGroups);
  };

  // -----------------------------------------------------------------------
  // Price-type helpers
  // -----------------------------------------------------------------------
  const applyZeroParentPrice = (groups, optPathKey, parentOptId, childMgOptions) => {
    if (!optPathKey) return;
    const segs = optPathKey.split('__');
    let parentMgPK = null;
    for (let i = segs.length - 1; i >= 0; i--) {
      if (segs[i].startsWith('op_')) {
        parentMgPK = segs.slice(0, i).join('__');
        break;
      }
    }
    if (!parentMgPK || !groups[parentMgPK]) return;
    const mg = groups[parentMgPK];
    if (mg.modifierOptions?.[parentOptId]) {
      const parentOptData = mg.modifierOptions[parentOptId];
      const multiplierChildren = Object.values(childMgOptions || {})
        .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
        .map((o) => ({ count: o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
      const parentBaseAQ =
        parentOptData.calculatedActualQuantity ??
        parentOptData.actualQuantityWithMultiplier ??
        parentOptData.originalPrice ?? 0;
      const aqResult = computeActualQtyUpdate({
        parentMgPathKey: parentMgPK,
        parentOptionId: parentOptId,
        parentBaseActualQty: parentBaseAQ,
        multiplierChildren,
        selectedModifierGroups: groups,
        menuItems: menuMap?.items ?? {},
        menuModifierGroups: menuMap?.modifierGroups ?? {},
      });
      groups[parentMgPK] = {
        ...mg,
        modifierOptions: {
          ...mg.modifierOptions,
          [parentOptId]: {
            ...parentOptData,
            price: 0,
            actualQuantityWithMultiplier: 0,
          },
        },
      };
      applyAncestorDispatch(groups, aqResult);
    }
  };

  const restoreParentPrice = (groups, optPathKey, parentOpt) => {
    if (!optPathKey || !parentOpt) return;
    const segs = optPathKey.split('__');
    let parentMgPK = null;
    for (let i = segs.length - 1; i >= 0; i--) {
      if (segs[i].startsWith('op_')) {
        parentMgPK = segs.slice(0, i).join('__');
        break;
      }
    }
    if (!parentMgPK || !groups[parentMgPK]) return;
    const mg = groups[parentMgPK];
    if (mg.modifierOptions?.[parentOpt._id]) {
      // If parent is also a multiplier option, restore to calculatedPrice
      // (originalPrice is the raw menu price, not the multiplied value)
      const restoredPrice =
        parentOpt.priceType === 'multiplierApplyingToParentPrice'
          ? (parentOpt.calculatedPrice ?? parentOpt.originalPrice ?? parentOpt.price ?? 0)
          : (parentOpt.originalPrice ?? parentOpt.price ?? 0);
      groups[parentMgPK] = {
        ...mg,
        modifierOptions: {
          ...mg.modifierOptions,
          [parentOpt._id]: {
            ...mg.modifierOptions[parentOpt._id],
            price: restoredPrice,
            actualQuantityWithMultiplier:
              mg.modifierOptions[parentOpt._id].calculatedActualQuantity ??
              mg.modifierOptions[parentOpt._id].actualQuantityWithMultiplier ??
              mg.modifierOptions[parentOpt._id].originalPrice ?? 0,
          },
        },
      };
    }
  };

  // -----------------------------------------------------------------------
  // Render – POS client v1 layout:
  //   Groups side-by-side (horizontal).
  //   Each group: header + column-first grid (top→bottom, max 3 rows, then next column).
  //   Salmon/pink buttons matching POS client modifier button images.
  // -----------------------------------------------------------------------
  const BTN_W = 130;
  const BTN_H = 74;
  const GAP = 4;
  const ROWS_PER_COL = 3;

  return (
    <Box sx={{ px: 1, py: 1 }}>
      {/* Section title – matches POS client "Modifier Groups" label */}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: POS_COLORS.BLACK,
          mb: 1,
        }}
      >
        Modifier Groups
      </Typography>

      {/* All modifier groups in a horizontal row (side by side) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: `${GAP}px`,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        {modifierGroupIds.map((mgId) => {
          const mgDef = menuMap?.modifierGroups?.[mgId];
          if (!mgDef) return null;

          const pathKey = buildPathKey(
            selectedProduct._id,
            mgId,
            effectiveParentPK
          );
          const mgSelection = modifierGroups[pathKey];
          const selectedOptions = mgSelection?.modifierOptions || {};

          const optionIds = mgDef.optionIds || [];
          const options = optionIds
            .map((oid) => {
              const item = menuMap?.items?.[oid];
              return item ? { ...item, _id: oid } : null;
            })
            .filter(Boolean);

          const mgMultiplierCond = {
            applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
            priceType: mgDef.priceType,
          };
          const mgMultiplierActive = isMultiplierConditionActive(mgMultiplierCond);
          const aggregatedOpts = mgDef.conditionIsAppliedForProduct
            ? findAllMgInstances(modifierGroups, mgDef._id).flatMap((pk) => {
                const inst = modifierGroups[pk];
                return inst ? Object.values(inst.modifierOptions ?? {}) : [];
              })
            : null;
          const totalSelected = getEffectiveOptionsCount(
            aggregatedOpts ?? Object.values(selectedOptions),
            mgDef.applyValidationToItemsActualQuantity,
            mgMultiplierCond
          );
          const minQty = mgDef.min || 0;
          const canMulti = mgDef.canSelectMultipleOptions === true;
          const mgMax = Number(mgDef.max) || 0;
          const minOk =
            (minQty <= 0 || totalSelected >= minQty) &&
            meetsModifierItemMinimums(
              mgDef,
              aggregatedOpts ?? Object.values(selectedOptions)
            );
          const maxOk = mgMax <= 0 || totalSelected <= mgMax;
          const isValid = minOk && maxOk;
          const showValidation =
            minQty > 0 || mgMax > 0 || hasModifierItemMinimums(mgDef);
          const isSingleSelect = mgMax === 1 && !canMulti && !mgMultiplierActive;
          const disableMax =
            !isSingleSelect && !mgMultiplierActive && mgMax > 0 && totalSelected >= mgMax;

          const optionsForCounting = aggregatedOpts ?? Object.values(selectedOptions);
          const totalItemCount = computeTotalItemCount(
            optionsForCounting,
            mgDef.applyValidationToItemsActualQuantity,
            mgMultiplierCond
          );
          const maxAnySingle = mgDef.maxAnySingleModifierItems ?? null;
          const maxTotalItems = mgDef.maxTotalNumberOfModifierItems ?? null;
          const isTotalItemsMaxReached =
            maxTotalItems != null && maxTotalItems > 0 && totalItemCount >= maxTotalItems;

          // Columns for this group (POS: ceil(options/3))
          const numCols = Math.max(1, Math.ceil(options.length / ROWS_PER_COL));
          const numRows = Math.min(options.length, ROWS_PER_COL);
          const headerW = numCols * BTN_W + (numCols - 1) * GAP;

          return (
            <Box key={mgId} sx={{ flexShrink: 0 }}>
              {/* Header – gray bar matching POS client */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: POS_COLORS.DISABLED_BG,
                  px: 1.5,
                  height: 37,
                  width: headerW,
                  borderRadius: '12px 12px 2px 2px',
                  boxSizing: 'border-box',
                  ...(showValidation && {
                    borderBottom: isValid
                      ? `2px solid ${POS_COLORS.GREEN}`
                      : `2px solid ${POS_COLORS.ERROR}`,
                  }),
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: 16,
                    fontWeight: 500,
                    color: POS_COLORS.BLACK,
                  }}
                >
                  {getTranslationValue(mgDef.name)}
                </Typography>

                {showValidation && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.3,
                      ml: 0.5,
                    }}
                  >
                    {isValid ? (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 18, color: POS_COLORS.GREEN }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{ fontSize: 18, color: POS_COLORS.ERROR }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: POS_COLORS.BLACK,
                      }}
                    >
                      {minQty}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Options – column-first grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gridTemplateRows: `repeat(${numRows}, ${BTN_H}px)`,
                  gridAutoColumns: `${BTN_W}px`,
                  gap: `${GAP}px`,
                  mt: `${GAP}px`,
                }}
              >
                {options.map((optionItem) => {
                  const optSel = selectedOptions[optionItem._id];
                  const optCount = optSel?.count || 0;
                  const isSelected = optCount > 0;
                  const hasNested = optionItem.modifierGroupIds?.length > 0;
                  const isSoldOut = isItemSoldOut(optionItem);
                  const isFreeOption = isSelected ? (optSel?.freeCount ?? 0) : 0;

                  // Calculate display price (POS client shows price on every option)
                  const menuOpt =
                    menuMap?.items?.[optionItem._id] || optionItem;

                  const optEffectiveCount = optSel
                    ? getEffectiveOptionCount(
                        optSel,
                        mgDef.applyValidationToItemsActualQuantity,
                        mgMultiplierCond
                      )
                    : 0;

                  let isDisabledByMax = (() => {
                    if (isTotalItemsMaxReached) return true;
                    if (
                      isSelected &&
                      maxAnySingle != null &&
                      maxAnySingle > 0 &&
                      optEffectiveCount >= maxAnySingle
                    ) return true;
                    if (
                      isSelected &&
                      mgDef.applyValidationToItemsActualQuantity &&
                      mgMax > 0 &&
                      totalSelected >= mgMax
                    ) return true;
                    if (!isSelected) {
                      if (mgMultiplierActive && mgMax > 0 &&
                        (mgDef.applyConditionWithMultiplier || mgMax === 1)) {
                        return false;
                      }
                      return disableMax;
                    }
                    return false;
                  })();
                  if (!isDisabledByMax && !isSelected) {
                    isDisabledByMax = shouldDisableForAncestorMax(
                      optionItem._id,
                      pathKey,
                      modifierGroups,
                      menuMap?.modifierGroups || {},
                      menuMap?.items || {}
                    );
                  }
                  const isDisabled = isSoldOut || isDisabledByMax;
                  const menuMg = menuMap?.modifierGroups?.[mgDef._id] || mgDef;
                  const priceCalc = calculatePriceByType({
                    option: {
                      ...menuOpt,
                      priceType: menuOpt?.priceType ?? optionItem.priceType,
                    },
                    modifierGroup: {
                      ...menuMg,
                      priceType: menuMg?.priceType ?? mgDef.priceType,
                    },
                    parentOption: isNestedLevel ? parentOption : null,
                    isNestedUnderParentOption: isNestedLevel,
                  });
                  // Check if option has multiplier priceType
                  const effectiveOptionPriceType = getEffectivePriceType(
                    {
                      ...menuOpt,
                      priceType: menuOpt?.priceType ?? optionItem.priceType,
                    },
                    {
                      ...menuMg,
                      priceType: menuMg?.priceType ?? mgDef.priceType,
                    }
                  );
                  const isMultiplierType =
                    effectiveOptionPriceType ===
                      PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE ||
                    effectiveOptionPriceType ===
                      PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;
                  // multiplierApplyingToParentPrice options should be single-select only (no quantity controls)
                  const isMultiplierToParent =
                    effectiveOptionPriceType ===
                    PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;
                  const menuPrice = menuOpt?.price ?? 0;

                  let displayPrice = 0;
                  const freshPrice = Number(priceCalc.optionPrice) || 0;
                  if (isSelected && optSel) {
                    if (priceCalc.shouldZeroParentPrice) {
                      displayPrice = freshPrice > 0
                        ? freshPrice
                        : Number(optSel.calculatedPrice) || 0;
                    } else {
                      const currentPrice = Number(optSel.price);
                      const storedCalc = Number(optSel.calculatedPrice);
                      if (currentPrice === 0 && storedCalc > 0 && !isMultiplierType) {
                        displayPrice = 0;
                      } else if (storedCalc > 0) {
                        displayPrice = storedCalc;
                      } else if (freshPrice > 0) {
                        displayPrice = freshPrice;
                      } else if (!isMultiplierType && menuPrice > 0) {
                        displayPrice = menuPrice;
                      }
                    }
                  } else {
                    if (freshPrice > 0) {
                      displayPrice = freshPrice;
                    } else if (!isMultiplierType && menuPrice > 0) {
                      displayPrice = menuPrice;
                    }
                  }

                  return (
                    <Box
                      key={optionItem._id}
                      onClick={() => {
                        if (isDisabled && !isSingleSelect) return;
                        handleOptionClick(mgDef, pathKey, optionItem);
                      }}
                      sx={{
                        position: 'relative',
                        borderRadius: '12px',
                        width: BTN_W,
                        height: BTN_H,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                        boxSizing: 'border-box',
                        border: isSelected
                          ? `2px solid ${POS_COLORS.GREEN}`
                          : '1px solid transparent',
                        bgcolor: isDisabled
                          ? POS_COLORS.DISABLED_BG
                          : isSelected
                          ? POS_COLORS.MODIFIER_BG_SELECTED
                          : POS_COLORS.MODIFIER_BG,
                        cursor: (isDisabled && !isSingleSelect) ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.7 : 1,
                        transition: 'all 0.15s ease',
                        userSelect: 'none',
                        '&:hover': {
                          bgcolor: isDisabled
                            ? undefined
                            : POS_COLORS.MODIFIER_BG_HOVER,
                        },
                      }}
                    >
                      {/* Selected check – top-right (always show for nested options) */}
                      {isSelected && (!canMulti || hasNested) && (
                        <CheckCircleIcon
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontSize: 20,
                            color: POS_COLORS.GREEN,
                          }}
                        />
                      )}

                      {isSoldOut && (
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 600,
                            color: POS_COLORS.ERROR,
                          }}
                        >
                          Unavailable
                        </Typography>
                      )}

                      {isFreeOption > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 8,
                            zIndex: 2,
                            backgroundColor: POS_COLORS.GREEN,
                            borderRadius: '4px',
                            px: '5px',
                            py: '2px',
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {isFreeOption === 1 ? 'FREE' : `${isFreeOption} FREE`}
                          </Typography>
                        </Box>
                      )}

                      {/* Option name + nested arrow on right (POS: Label row with arrowForward) */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          flex: 1,
                          minHeight: 0,
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 500,
                            color: POS_COLORS.BLACK,
                            fontSize: 16,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                            textAlign: 'center',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {getTranslationValue(optionItem.name)}
                        </Typography>
                        {hasNested && (
                          <ChevronRightIcon
                            sx={{
                              position: 'absolute',
                              right: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: 20,
                              color: POS_COLORS.BLACK,
                            }}
                          />
                        )}
                      </Box>
                      {/* Price display: multiplier types show "Nx" before selection, "$X.XX" after */}
                      {isMultiplierType && !isSelected && menuPrice > 0 ? (
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: POS_COLORS.BLACK_SECONDARY,
                            mt: 0.2,
                          }}
                        >
                          {menuPrice}x
                        </Typography>
                      ) : displayPrice !== 0 ? (
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: POS_COLORS.BLACK_SECONDARY,
                            mt: 0.2,
                          }}
                        >
                          {displayPrice > 0 ? '+' : ''}$
                          {Math.abs(displayPrice).toFixed(2)}
                        </Typography>
                      ) : null}
                      {/* Multiplier count for selected nested options (matches POS "1x" badge) */}
                      {isSelected && hasNested && (
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: POS_COLORS.BLACK_SECONDARY,
                            mt: 0.2,
                          }}
                        >
                          {optCount}x
                        </Typography>
                      )}

                      {/* +/- controls (canSelectMultipleOptions) */}
                      {isSelected && canMulti && !hasNested && !isMultiplierToParent && !mgMultiplierActive && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.3,
                            mt: 0.3,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0.2,
                              border: `1px solid ${POS_COLORS.BLACK}`,
                              borderRadius: '50%',
                            }}
                            onClick={() =>
                              handleDecrement(mgDef, pathKey, optionItem._id)
                            }
                          >
                            <RemoveIcon
                              sx={{ fontSize: 14, color: POS_COLORS.BLACK }}
                            />
                          </IconButton>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              minWidth: 16,
                              textAlign: 'center',
                              color: POS_COLORS.BLACK,
                            }}
                          >
                            {optCount}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={isDisabled || isDisabledByMax}
                            sx={{
                              p: 0.2,
                              border: `1px solid ${POS_COLORS.BLACK}`,
                              borderRadius: '50%',
                              '&.Mui-disabled': { opacity: 0.4 },
                            }}
                            onClick={() =>
                              handleIncrement(mgDef, pathKey, optionItem._id)
                            }
                          >
                            <AddIcon
                              sx={{ fontSize: 14, color: POS_COLORS.BLACK }}
                            />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ModifierGroupsPanel;
